import { PropsWithChildren } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { useCurrentLeagueContext } from "@/contexts/CurrentLeagueContext";
import { useCurrentOrganizationContext } from "@/contexts/CurrentOrganizationContext";
import { useCurrentPlatformContext } from "@/contexts/CurrentPlatformContext";
import { buildScopedPath, isAlreadyScopedPath } from "@/utils/common/scoped-routes";

/**
 * Strip a legacy `/dashboard` segment from the path so old links like
 * `/dashboard/sports` canonicalize to `/sports` (which the enforcer below
 * then redirects to the scoped form).
 */
const stripLegacyDashboardPrefix = (pathname: string): string =>
  pathname.replace(/^\/dashboard(\/|$)/, "/");

export const ScopedRouteEnforcer = ({ children }: PropsWithChildren) => {
  const { platform } = useCurrentPlatformContext();
  const { organization } = useCurrentOrganizationContext();
  const { league } = useCurrentLeagueContext();
  const location = useLocation();

  const canonicalPathname = stripLegacyDashboardPrefix(location.pathname);
  const needsDashboardStrip = canonicalPathname !== location.pathname;

  // The URL is the source of truth: an already-scoped URL (carries the
  // `/platform/:p` prefix) is honored as-is — the contexts follow it. Only
  // canonicalize UNSCOPED URLs to the depth implied by the current context.
  if (isAlreadyScopedPath(location.pathname) && !needsDashboardStrip) {
    return <>{children}</>;
  }

  // Platform not resolved yet → render children (the splash screen is still up).
  if (!platform) {
    if (needsDashboardStrip) {
      return <Navigate to={`${canonicalPathname}${location.search}${location.hash}`} replace />;
    }
    return <>{children}</>;
  }

  const restPath = canonicalPathname.replace(/^\/+/, "");
  const scopedPath = buildScopedPath(
    platform.id,
    organization?.id ?? null,
    league?.id ?? null,
    restPath
  );
  const target = `${scopedPath}${location.search}${location.hash}`;

  if (target === `${location.pathname}${location.search}${location.hash}`) {
    return <>{children}</>;
  }

  return <Navigate to={target} replace />;
};
